<template>
  <div>
    <div
      class="track"
      :class="{'is-active': tag === selection.tag && mode === 'editing', 'is-selected': tag === selection.tag, 'is-collapsed': !showTrack }"
      @click="onSelectTag"
    >
      <div class="track__wrapper">
        <div class="track__info">
          <div class="track__info-controls">
            <i
              v-if="showTrack"
              @click="showTrack=!showTrack"
              class="material-icons track__collapse-icon"
            >arrow_drop_down</i>
            <i
              v-else
              @click="showTrack=!showTrack"
              class="material-icons track__collapse-icon"
            >arrow_right</i>
            <i class="material-icons track__delete" @click.stop="onDeleteTag(tag)">delete_forever</i>
            <i class="material-icons track__clone" @click.stop="onCopyTag(tag)">file_copy</i>
          </div>
          <div class="track__info-text">
            <input type="text" class="track__track-name track__type" v-model="trackName" />
            <i
              class="material-icons track__tag-type"
              v-tooltip.bottom-center="metaData.label"
            >{{metaData.icon}}</i>
          </div>
        </div>
        <div class="track__scenes" ref="track" :style="scenesContainerStyle">
          <track-scene
            v-for="(scene, index) in tag.scenes"
            :key="scene.name + '-' + index"
            :class="{'is-active': scene === selectedScene}"
            :style="sceneStyle(scene)"
            :scene="scene"
            @click.native="onSceneClick(scene, $event)"
          >
            <track-waypoint
              v-for="(wp, wpIndex) in scene.waypoints"
              :key="'scene' + index + '-wp-' + wpIndex"
              :class="{'is-active': wp === selectedWaypoint, 'last-has-action': wpIndex === (scene.waypoints.length - 1) && lastWaypointAction}"
              :style="waypointStyle(wp, scene)"
              :wp="wp"
              :index="wpIndex"
              :hasAction="wpIndex === (scene.waypoints.length - 1) && lastWaypointAction"
              :actionLabel="lastWaypointAction"
              @moved="e => onWaypointMoved(wp, e)"
              @stopMove="onStopMove(scene)"
            />
            <span
              v-if="hasOnClickBehaviour"
              class="on-click-indicator"
              v-tooltip.bottom-center="behaviourLabel"
            >
              <i class="material-icons">touch_app</i>
            </span>
          </track-scene>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex";
import TrackScene from "./trackScene";
import TrackWaypoint from "./trackWaypoint";
import {
  metaDataFormDefaultsFor,
  metaDataFormDefaults
} from "./metadataFormFields";

const sceneStartTime = scene => Math.min(...scene.waypoints.map(w => w.time));
const sceneEndTime = scene => Math.max(...scene.waypoints.map(w => w.time));

export default {
  name: "tagTrack",

  components: {
    "track-scene": TrackScene,
    "track-waypoint": TrackWaypoint
  },

  props: {
    tag: {},

    zoom: {
      type: Object,
      default: () => {
        return {
          ratio: 1,
          center: 0,
          left: 0
        };
      }
    },

    keyFramesTimes: {
      type: Array,
      default: () => {
        return [];
      }
    }
  },

  data: function() {
    return {
      showTrack: true
    };
  },

  computed: {
    ...mapState(["selection", "mode"]),
    ...mapGetters(["videoDuration"]),

    metaData: function() {
      const template = this.tag.template || "ptag";

      const metaData = metaDataFormDefaults().find(mdfd => {
        return mdfd.key === template;
      });

      return metaData || { icon: "done", label: "Waypoint" };
    },

    // AH: changed to selectedScene, conflicts with selectScene mutator and intention seems to get the selected scene
    selectedScene: function() {
      return this.selection.scene;
    },

    selectedWaypoint: function() {
      return this.selection.waypoint;
    },

    trackName: {
      get: function() {
        const name = this.tag.name;
        const template = this.lookupTemplateLabel(this.tag.template);

        const label = name !== "New Tag" ? name : template || "";

        return label.length > 24 ? label.substr(0, 24) + "..." : label;
      },

      set: function(newName) {
        this.tag.name = newName;
      }
    },

    scenesContainerStyle: function() {
      const left = -this.zoom.left * 100 * this.zoom.ratio + "%";
      const width = `calc(${this.zoom.ratio * 100}% - 124px)`;

      return {
        left: left,
        width: width
      };
    },

    lastWaypointAction() {
      return this.tag.meta.onLastFrame &&
        this.tag.meta.onLastFrame.action &&
        this.tag.meta.onLastFrame.action.length > 0
        ? this.tag.meta.onLastFrame.action
        : null;
    },

    hasOnClickBehaviour() {
      return (
        this.tag.meta.behaviour &&
        this.tag.meta.behaviour.action &&
        this.tag.meta.behaviour.action.length > 0
      );
    },

    behaviourLabel() {
      let action =
        this.tag.meta.behaviour && this.tag.meta.behaviour.action
          ? this.tag.meta.behaviour.action
          : "";
      return `has an on click action: ${action}`;
    }
  },

  methods: {
    ...mapMutations([
      "setMode",
      "selectTag",
      "selectScene",
      "deleteTag",
      "copyTag",
      "setPlayerTime"
    ]),

    sceneStyle: function(scene) {
      const startTime = sceneStartTime(scene);
      const left = this.relativeTimePosition(startTime) * 100 + "%";
      const width =
        this.relativeTimePosition(sceneEndTime(scene) - startTime) * 100 + "%";

      return {
        left: left,
        width: width
      };
    },

    waypointStyle: function(waypoint, scene) {
      const startTime = sceneStartTime(scene);
      const left =
        ((waypoint.time - startTime) / (sceneEndTime(scene) - startTime)) *
          100 +
        "%";

      return {
        left: left
      };
    },

    onSelectTag: function(ev) {
      // tag aleady selected no need to reaply again
      if (this.tag === this.selection.tag) {
        return;
      }

      // we want a toggling behaviour on the tag tracks. if the tag ( track ) is selected and we click outside the scene (on the track) the tag should be closed
      if (ev.target === this.$refs.track && this.tag === this.selection.tag) {
        this.closeAndSaveTag();
        return;
      }

      let boundingBox = this.$refs.track.getBoundingClientRect();
      let p = (ev.clientX - boundingBox.left) / boundingBox.width;

      this.selectTag({ tag: this.tag, atRelativeTimePos: p });
      const first = this.selection.tag.scenes
        ? this.selection.tag.scenes[0]
        : null;
      this.selectScene(first);
      this.setMode("editing");
      this.emitTrackSelected();
    },

    onCopyTag: function(tag) {
      this.copyTag(tag);
    },

    onDeleteTag: function(tag) {
      const options = { title: "Delete track?", cancelLabel: "Cancel" };
      this.$dialogs
        .confirm("Do you really want to delete this track?", options)
        .then(res => {
          if (res.ok) {
            this.deleteTag(tag);
            this.setMode(null);
          }
        });
    },

    onWaypointMoved: function(waypoint, e) {
      const cx = e.cx;
      const boundingBox = this.$refs.track.getBoundingClientRect();
      let displayedPosition = (cx - boundingBox.left) / boundingBox.width;

      //Assure that displayPosition isn't smaller 0 or higher 1
      displayedPosition = displayedPosition < 0 ? 0 : displayedPosition;
      displayedPosition = displayedPosition > 1 ? 1 : displayedPosition;

      const time = displayedPosition * this.videoDuration;

      let newTime = time;

      if (e.ctrlKey) {
        let prevKft = 0;

        this.keyFramesTimes.forEach(kft => {
          const threshold = (kft + prevKft) / 2;

          if (threshold <= time) {
            newTime = kft;
          }

          prevKft = kft;
        });
      }

      waypoint.time = newTime;
      this.setPlayerTime(waypoint.time);
    },

    onStopMove: function(scene) {
      if (scene !== this.selectedScene) {
        this.selectScene(scene);
      }

      this.selectedScene.waypoints.sort((a, b) => {
        return a.time - b.time;
      });
    },

    onSceneClick: function(scene, e) {
      if (scene !== this.selectedScene) {
        this.selectScene(scene);
        this.setMode("editing");
        e.stopPropagation();
      }
    },

    emitTrackSelected: function() {
      this.$emit("trackSelected");
    },

    closeAndSaveTag: function() {
      this.$emit("saveScene");
    },

    relativeTimePosition: function(time) {
      return this.videoDuration ? time / this.videoDuration : 0;
    },

    lookupTemplateLabel: function(template) {
      return (metaDataFormDefaultsFor(template) || {}).label;
    }
  }
};
</script>
